import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { DataDictionary } from '../../model/data-dictionary';
import { HisBackendService } from '../backend/his-backend.service';
import { HisCacheService } from '../backend/his-cache.service';
import { HisUserProfileService } from '../backend/his-userprofile.service';
import { HisBaseGlobalsService } from './his-base-globals.service';

/**
 * initializes a HIS Angular application.
 */
@Injectable({providedIn: 'root'})
export class HisBootstrapService  {
    private hisCacheService = inject(HisCacheService);
    private hisBaseGlobalsService = inject(HisBaseGlobalsService);
    private hisBackendService = inject(HisBackendService);
    private hisUserProfileService = inject(HisUserProfileService);


    private promise: Promise<boolean>;
    private uiLoaded = false;
    private ddLoaded = false;
    private upLoaded = false;

    resolve( route: ActivatedRouteSnapshot, state: RouterStateSnapshot ): Promise<any> {
        console.log( 'HisBootstrapService.resolve()' );
        return this.boot();
    }

    /**
     * Starts the HISinOne Angular Application.
     *
     * @return a promise that returns true, when the user interface should start to render
     */
    boot(): Promise<boolean> {
        // IMPORTANT: This code is executed before the user interface is rendered,
        //            therefore it should be as fast as possible. Please think twice
        //            before adding code here: Does it really need to run before
        //            the user interface starts rendering?

        if ( this.promise ) {
            return this.promise;
        }

        this.promise = new Promise<boolean>( ( resolvePromise ) => {
            let globalProperties = this.readGlobalPropertiesFromHtml();
            this.hisBaseGlobalsService.setGlobalProperties( globalProperties );
            this.hisCacheService.setServerVersions( globalProperties.serverVersions );

            // get uitexts for the found language
            let url = 'api/v1/cs/sys/uitexts/' + globalProperties.user.language;
            let clientVersionKey = 'version:' + url;
            this.hisCacheService.getDataCachedByIndexedDB2( url, clientVersionKey, HisCacheService.UITEXTS_SERVER_VERSION_KEY )
                .subscribe( ( ui: Record<string, string> ) => {
                    if ( Object.keys( ui ).length !== 0 ) {
                        this.hisBaseGlobalsService.setUi( ui );
                        this.uiLoaded = true;
                        this.checkDone( resolvePromise );
                    }
                } );

            this.hisBackendService.getData( 'api/v1/cs/sys/datadictionary/allContexts' ).subscribe( ( dd: DataDictionary ) => {
                if ( Object.keys( dd ).length !== 0 ) {
                    this.hisBaseGlobalsService.setDd( dd );
                    this.ddLoaded = true;
                    this.checkDone( resolvePromise );
                }
            } );
            
            /** 
             * UserProfile Enhancement #338030
             * Initially fetches the complete userprofile and caches it in a variable when page loads
             * to prevent multiple requests from individual components to backend.
             */
            this.hisUserProfileService.preloadUserProfile().subscribe( () => {
                this.upLoaded = true;
                this.checkDone( resolvePromise );
            });
            
        } );

        return this.promise;
    }

    private checkDone( resolvePromise: ( value?: boolean | PromiseLike<boolean> ) => void ) {
        if ( this.uiLoaded && this.ddLoaded && this.upLoaded ) {
            console.log( 'HisBootstrapService done' );
            resolvePromise( true );
        }
    }

    readGlobalPropertiesFromHtml(): any {
        let element = document.getElementById( 'hisglobalproperties' );
        if ( !element ) {
            console.warn( 'cannot retrieve global properties, the Angular extension seems not to be called from within HISinOne.' );
            return {
                user: {
                    language: 'de'
                }
            };
        }
        let json = element.textContent;
        return JSON.parse( json );
    }
}
